import { IStairCalculationResult, IStairConfig } from '@stair-craft/common/util';

import { IPaneRect } from './plan-primitive';
import { ESheetViews, ISheet, SHEET_SIZE, buildSheetLayout, buildSideSheetLayout } from './sheet-layout';
import { ITextMeasurer } from './text-measurer';

/** Space kept between the canvas edge (or the controls) and the fitted sheet frame, pixels. */
const FIT_MARGIN: number = 8;

/** How the fixed sheet is shown in a canvas: sheet pixel → canvas pixel is `x + p · scale`. */
export interface ISheetFit {
    scale: number;
    x: number;
    y: number;
}

/** Whether two rectangles share any area. */
export function rectsMeet(a: IPaneRect, b: IPaneRect): boolean {
    return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

/** The frame as it lands in the canvas under a fit. */
export function fittedFrame(frame: IPaneRect, fit: ISheetFit): IPaneRect {
    return { x: fit.x + frame.x * fit.scale, y: fit.y + frame.y * fit.scale, w: frame.w * fit.scale, h: frame.h * fit.scale };
}

function centredIn(frame: IPaneRect, region: IPaneRect): ISheetFit {
    const scale: number = Math.max(0, Math.min((region.w - 2 * FIT_MARGIN) / frame.w, (region.h - 2 * FIT_MARGIN) / frame.h));
    return {
        scale,
        x: region.x + (region.w - frame.w * scale) / 2 - frame.x * scale,
        y: region.y + (region.h - frame.h * scale) / 2 - frame.y * scale,
    };
}

/**
 * The initial view of a sheet in a canvas, as a drawing viewer opens a file: the whole frame,
 * as large as it goes and centred, clear of `reserve` — the corner under the canvas controls.
 * The frame is tried in the whole canvas, in the band above the controls and in the band left
 * of them; the largest fit that keeps clear of the corner wins.
 */
export function fitSheet(frame: IPaneRect, canvas: { width: number; height: number }, reserve: IPaneRect | null): ISheetFit {
    const whole: IPaneRect = { x: 0, y: 0, w: canvas.width, h: canvas.height };
    const regions: IPaneRect[] = reserve
        ? [whole, { ...whole, h: Math.max(0, reserve.y) }, { ...whole, w: Math.max(0, reserve.x) }]
        : [whole];
    let best: ISheetFit | null = null;
    for (const region of regions) {
        const fit: ISheetFit = centredIn(frame, region);
        const clear: boolean = reserve === null || !rectsMeet(fittedFrame(frame, fit), reserve);
        if (clear && fit.scale > 0 && (!best || fit.scale > best.scale)) {
            best = fit;
        }
    }
    return best ?? centredIn(frame, whole);
}

/**
 * The «План» sheet as the plan view shows it: the top view and the details, laid out once on the
 * fixed sheet, whatever the canvas. The front and side view live on the «Фасады» sheet.
 */
export function planSheet(result: IStairCalculationResult, config: IStairConfig, measurer: ITextMeasurer): ISheet {
    return buildSheetLayout(result, config, SHEET_SIZE, measurer, ESheetViews.TopOnly);
}

/** The «Фасады» sheet as the plan view shows it: laid out once on the fixed sheet, whatever the canvas. */
export function elevationsSheet(result: IStairCalculationResult, config: IStairConfig, measurer: ITextMeasurer): ISheet {
    return buildSideSheetLayout(result, config, SHEET_SIZE, measurer);
}
